import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

export default function LocationAutocomplete({ id, value, onChange, placeholder, className, required }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (q) => {
    clearTimeout(timer.current);
    if (!q || q.length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(q)}`
        );
        const data = await res.json();
        setResults(data || []);
        setOpen((data || []).length > 0);
      } catch {
        setResults([]);
      }
    }, 450);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        id={id}
        value={value}
        placeholder={placeholder || "Search a place or address..."}
        className={className}
        required={required}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => results.length > 0 && setOpen(true)}
        data-testid={`${id}-autocomplete-input`}
      />
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-white/10 bg-card shadow-xl max-h-56 overflow-y-auto"
          data-testid="location-suggestions"
        >
          {results.map((r) => (
            <button
              key={r.place_id}
              type="button"
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-primary/10 flex items-start gap-2"
              onClick={() => {
                onChange(r.display_name);
                setOpen(false);
                setResults([]);
              }}
              data-testid="location-suggestion-item"
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
              <span className="line-clamp-2">{r.display_name}</span>
            </button>
          ))}
          <p className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-white/5">
            Powered by OpenStreetMap
          </p>
        </div>
      )}
    </div>
  );
}
